import React, { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { Modal, Button, Select, Input, Field, Spinner } from "../ui";
import { Plus, Trash2, Repeat, Power, Play, CalendarClock } from "lucide-react";
import { cn } from "../../lib/utils";

const FREQS = ["daily", "weekly", "monthly", "quarterly", "annually"];

function emptyTemplate(board) {
  const today = new Date().toISOString().slice(0, 10);
  return { name: "", freq: "monthly", group_id: board.groups[0]?.id, next_run: today, values: {}, enabled: true };
}

export default function RecurringModal({ board, onClose, onSpawned }) {
  const { t, toast } = useApp();
  const [recs, setRecs] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/boards/${board.id}/recurrences`); setRecs(data); }
    catch { setRecs([]); }
  }, [board.id]);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditId(null); setDraft(emptyTemplate(board)); };
  const startEdit = (r) => { setEditId(r.id); setDraft(JSON.parse(JSON.stringify(r))); };

  const save = async () => {
    if (!draft.name?.trim()) { toast(t("recur_name_required")); return; }
    setBusy(true);
    try {
      const payload = { name: draft.name.trim(), freq: draft.freq, group_id: draft.group_id,
        next_run: draft.next_run, values: draft.values || {}, enabled: draft.enabled };
      if (editId) await api.put(`/board-recurrences/${editId}`, payload);
      else await api.post(`/boards/${board.id}/recurrences`, payload);
      setDraft(null); setEditId(null); toast(t("recur_saved")); load();
    } catch (e) { toast(e?.response?.data?.detail || t("recur_save_failed")); }
    finally { setBusy(false); }
  };

  const toggle = async (r) => {
    await api.put(`/board-recurrences/${r.id}`, { name: r.name, freq: r.freq, group_id: r.group_id, next_run: r.next_run, values: r.values, enabled: !r.enabled });
    load();
  };
  const del = async (r) => { await api.delete(`/board-recurrences/${r.id}`); toast(t("recur_deleted")); load(); };
  const runNow = async (r) => {
    const { data } = await api.post(`/board-recurrences/${r.id}/run`);
    if (data?.item) onSpawned?.(data.item);
    toast(t("recur_ran"));
    load();
  };

  const groupName = (id) => board.groups.find((g) => g.id === id)?.name || "?";

  return (
    <Modal open onClose={onClose} title={<span className="inline-flex items-center gap-2"><Repeat className="w-4 h-4 text-sky-400" /> {t("recur_title")}</span>}
      footer={draft ? (
        <>
          <Button variant="outline" onClick={() => { setDraft(null); setEditId(null); }} data-testid="recur-cancel">{t("cancel")}</Button>
          <Button onClick={save} disabled={busy || !draft.name?.trim()} data-testid="recur-save">{busy ? t("loading") : t("save")}</Button>
        </>
      ) : <Button onClick={onClose}>{t("done") || "Done"}</Button>}>
      {recs === null ? (
        <div className="py-8 flex justify-center"><Spinner className="w-6 h-6 text-text-secondary" /></div>
      ) : draft ? (
        <div className="space-y-4" data-testid="recur-builder">
          <Field label={t("recur_name")}>
            <Input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder={t("recur_name_placeholder")} data-testid="recur-name" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("recur_frequency")}>
              <Select value={draft.freq} onChange={(e) => setDraft((d) => ({ ...d, freq: e.target.value }))} data-testid="recur-freq">
                {FREQS.map((f) => <option key={f} value={f}>{t(`recur_${f}`)}</option>)}
              </Select>
            </Field>
            <Field label={t("recur_next_run")}>
              <Input type="date" value={(draft.next_run || "").slice(0, 10)} onChange={(e) => setDraft((d) => ({ ...d, next_run: e.target.value }))} data-testid="recur-next-run" />
            </Field>
          </div>
          <Field label={t("recur_into_group")}>
            <Select value={draft.group_id || ""} onChange={(e) => setDraft((d) => ({ ...d, group_id: e.target.value }))} data-testid="recur-group">
              {board.groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </Select>
          </Field>
        </div>
      ) : (
        <div className="space-y-3" data-testid="recur-list">
          {recs.length === 0 && (
            <div className="text-center py-8">
              <Repeat className="w-8 h-8 text-text-secondary/40 mx-auto mb-2" />
              <p className="text-sm text-text-secondary">{t("recur_empty")}</p>
            </div>
          )}
          {recs.map((r) => (
            <div key={r.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl border" data-testid={`recur-rule-${r.id}`}>
              <button onClick={() => toggle(r)} data-testid={`recur-toggle-${r.id}`} title={r.enabled ? t("autom_enabled") : t("autom_disabled")}
                className={cn("shrink-0 transition-colors", r.enabled ? "text-emerald-400" : "text-text-secondary/50")}>
                <Power className="w-4 h-4" />
              </button>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{r.name}</div>
                <div className={cn("text-xs truncate flex items-center gap-1.5", r.enabled ? "text-text-secondary" : "text-text-secondary/50")}>
                  <span>{t(`recur_${r.freq}`)}</span><span>·</span><span>{groupName(r.group_id)}</span>
                  <span>·</span><CalendarClock className="w-3 h-3" /><span>{(r.next_run || "").slice(0, 10)}</span>
                </div>
              </div>
              <button onClick={() => runNow(r)} data-testid={`recur-run-${r.id}`} title={t("recur_run_now")} className="text-text-secondary hover:text-primary"><Play className="w-4 h-4" /></button>
              <button onClick={() => startEdit(r)} data-testid={`recur-edit-${r.id}`} className="text-xs text-text-secondary hover:text-text-primary">{t("edit")}</button>
              <button onClick={() => del(r)} data-testid={`recur-delete-${r.id}`} className="text-text-secondary hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <Button variant="outline" className="w-full" onClick={startNew} data-testid="recur-new"><Plus className="w-4 h-4 mr-1.5" />{t("recur_new")}</Button>
        </div>
      )}
    </Modal>
  );
}
